import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CheckCircle2, Play, ChevronRight, Lock, PenLine } from "lucide-react";

export default function SubjectView() {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [subject, setSubject] = useState(null);
  const isStudent = role === "student";

  useEffect(() => {
    api.getSubject(subjectId).then(setSubject).catch(() => setSubject(false));
  }, [subjectId]);

  if (subject === false) return <div className="p-16 text-center font-heading text-xl text-[#6B7280]">Subject not found.</div>;
  if (!subject) return <div className="p-16 text-center font-cursive text-2xl text-[#0D9488] animate-pulse">Loading…</div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
      <button onClick={() => navigate("/")} data-testid="back-to-hub" className="mb-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold text-[#6B7280] hover:bg-[#F2ECE1]">
        <ArrowLeft size={16} /> All Subjects
      </button>

      <div className="flex items-center gap-3">
        <div className="h-3 w-3 rounded-full" style={{ background: subject.color }} />
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1F2937]">{subject.name}</h1>
      </div>
      <p className="mt-1 font-body text-base text-[#6B7280]">{subject.tagline}</p>

      {subject.cursive_link && (
        <button onClick={() => navigate(subject.cursive_link)} data-testid="cursive-studio-card"
          className="group mt-6 flex w-full items-center gap-4 rounded-3xl border-2 border-[#0D9488]/30 bg-[#0D9488]/5 p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#0D9488] text-white shadow-sm"><PenLine size={26} /></div>
          <div className="min-w-0 flex-1">
            <div className="font-heading text-lg font-extrabold text-[#1F2937]">Cursive Studio</div>
            <p className="font-body text-sm text-[#6B7280]">8 levels of cursive letters, words and sentences with live tracing feedback.</p>
          </div>
          <ChevronRight size={22} className="text-[#0D9488] transition-transform group-hover:translate-x-1" />
        </button>
      )}

      {subject.domains.length === 0 && (
        <p className="mt-8 rounded-2xl border border-dashed border-[#E5DECF] p-6 text-center font-body text-sm text-[#94A3B8]" data-testid="subject-empty">
          {isStudent ? "No handwriting lessons have been turned on for you yet." : "No lessons here yet — add some in the Lesson Studio or turn them on in the Library."}
        </p>
      )}

      <div className="mt-8 space-y-8">
        {subject.domains.map((d) => (
          <div key={d.id}>
            <h2 className="font-heading text-lg font-bold text-[#1F2937]">{d.name}</h2>
            {d.units.map((u) => (
              <div key={u.id} className="mt-3">
                <div className="mb-2 font-body text-xs font-bold uppercase tracking-wide text-[#94A3B8]">{u.name}</div>
                <div className="space-y-3">
                  {u.lessons.map((l, i) => (
                    <motion.button
                      key={l.id}
                      initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }}
                      onClick={() => {
                        if (l.locked) {
                          if (isStudent) toast("Ask a grown-up to unlock this lesson for you 🌟");
                          else navigate("/pricing");
                        } else {
                          navigate(`/learn/${l.id}`);
                        }
                      }}
                      data-testid={`lesson-card-${l.id}`}
                      className={`flex w-full items-center gap-4 rounded-3xl border p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-md ${l.locked ? "border-[#E5DECF] bg-[#F2ECE1]/40" : "border-[#E5DECF] bg-white"}`}>
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white" style={{ background: l.locked ? "#94A3B8" : subject.color }}>
                        {l.locked ? <Lock size={20} /> : l.completed ? <CheckCircle2 size={24} /> : <Play size={22} />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-heading text-lg font-bold text-[#1F2937]">{l.title}</span>
                          {l.free && <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#047857]">Free</span>}
                          {l.locked && !isStudent && <span className="rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#B45309]">Premium</span>}
                          {l.tag && <span className="rounded-full bg-[#F2ECE1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#6B7280]">{l.tag}</span>}
                        </div>
                        <p className="mt-0.5 break-words font-body text-sm text-[#6B7280]">{l.objective}</p>
                        {!l.locked && (
                          <div className="mt-2 flex items-center gap-3">
                            <div className="h-2 w-28 overflow-hidden rounded-full bg-[#F2ECE1]">
                              <div className="h-full rounded-full" style={{ width: `${l.progress_percentage}%`, background: subject.color }} />
                            </div>
                            <span className="text-xs font-bold text-[#6B7280]">{l.activities_completed}/{l.activities_total} activities</span>
                          </div>
                        )}
                        {l.locked && (
                          <div className="mt-1 font-body text-xs font-semibold text-[#B45309]" data-testid={`lesson-locked-${l.id}`}>
                            {isStudent ? "Ask a grown-up to unlock ✨" : "Upgrade to unlock →"}
                          </div>
                        )}
                      </div>
                      <ChevronRight size={20} className="shrink-0 text-[#94A3B8]" />
                    </motion.button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
